//@flow
import {IconButton, debounce, makeStyles} from '@material-ui/core';
import React, {useEffect, useRef, useState} from 'react';
import {colors} from '../../assets/styles/colors';
import {ArrowForwardIos} from '@material-ui/icons';
import {classNames} from '../../utils/react';

type Props = {
  children?: React$Node,
  classes?: {
    root?: string,
    scrollContainer?: string,
    arrowButton?: string,
  },
};

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  scrollContainer: {
    display: 'flex',
    flexWrap: 'nowrap',
    overflowX: 'auto',
    overflowY: 'hidden',
    width: '100%',
    height: '100%',
    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
    },

    '&::-webkit-scrollbar-track': {
      background: colors.grey200,
      borderRadius: '5px',
    },

    '&::-webkit-scrollbar-thumb': {
      background: colors.grey400,
      borderRadius: '5px',
    },
  },
  arrowButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    backdropFilter: 'blur(5px)',
    background: 'rgba(255, 255, 255, .5)',

    '&[data-position="left"]': {
      left: '.25rem',
      transform: 'translateY(-50%) rotate(180deg)',
    },
    '&[data-position="right"]': {
      right: '.25rem',
    },
  },
}));

export default function CustomHorizontalScroll(props: Props) {
  const {children, classes: propClasses = {}} = props;

  const classes = useStyles();

  const [hasLeftScroll, setHasLeftScroll] = useState(false);
  const [hasRightScroll, setHasRightScroll] = useState(false);

  useEffect(() => {
    handleScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  const scrollContainerRef = useRef(null);

  const rootClasses = classNames(classes.root, propClasses.root);
  const scrollContainerClasses = classNames(
    classes.scrollContainer,
    propClasses.scrollContainer
  );

  const arrowButtonClasses = classNames(
    classes.arrowButton,
    propClasses.arrowButton
  );

  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      const tableWidth = scrollContainerRef.current.offsetWidth / 2;
      scrollContainerRef.current.scrollTo({
        left: scrollContainerRef.current.scrollLeft - tableWidth,
        behavior: 'smooth',
      });
    }
  };

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      const tableWidth = scrollContainerRef.current.offsetWidth / 2;
      scrollContainerRef.current.scrollTo({
        left: scrollContainerRef.current.scrollLeft + tableWidth,
        behavior: 'smooth',
      });
    }
  };

  const handleScroll = debounce(() => {
    if (!scrollContainerRef.current) return;
    const {scrollLeft, scrollWidth, offsetWidth} = scrollContainerRef.current;

    const hasLeftScroll = scrollLeft > 0;
    const hasRightScroll = scrollLeft + offsetWidth < scrollWidth;

    setHasLeftScroll(hasLeftScroll);
    setHasRightScroll(hasRightScroll);
  }, 100);

  return (
    <div className={rootClasses}>
      {hasLeftScroll && (
        <IconButton
          size="small"
          className={arrowButtonClasses}
          data-position="left"
          onClick={handleScrollLeft}
        >
          <ArrowForwardIos fontSize="small" />
        </IconButton>
      )}
      <div
        className={scrollContainerClasses}
        ref={scrollContainerRef}
        onScroll={e => {
          handleScroll();
        }}
      >
        {children}
      </div>

      {hasRightScroll && (
        <IconButton
          size="small"
          className={arrowButtonClasses}
          data-position="right"
          onClick={handleScrollRight}
        >
          <ArrowForwardIos fontSize="small" />
        </IconButton>
      )}
    </div>
  );
}
